import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import messages from '../i18n/messages';

export const CURRENCIES = [
  { code: 'QAR', name: 'Qatari Riyal', nameAr: 'ريال قطري' },
  { code: 'USD', name: 'US Dollar', nameAr: 'دولار أمريكي' },
  { code: 'EUR', name: 'Euro', nameAr: 'يورو' },
  { code: 'GBP', name: 'British Pound', nameAr: 'جنيه إسترليني' },
  { code: 'SAR', name: 'Saudi Riyal', nameAr: 'ريال سعودي' },
  { code: 'AED', name: 'UAE Dirham', nameAr: 'درهم إماراتي' },
  { code: 'KWD', name: 'Kuwaiti Dinar', nameAr: 'دينار كويتي' },
  { code: 'BHD', name: 'Bahraini Dinar', nameAr: 'دينار بحريني' },
  { code: 'OMR', name: 'Omani Rial', nameAr: 'ريال عُماني' },
  { code: 'EGP', name: 'Egyptian Pound', nameAr: 'جنيه مصري' },
];

const ARAB_COUNTRIES = new Set([
  'QA', 'AE', 'SA', 'KW', 'BH', 'OM', 'EG', 'JO', 'LB', 'IQ', 'SY', 'YE', 'PS',
  'SD', 'LY', 'TN', 'DZ', 'MA', 'MR',
]);

const FALLBACK_RATES = {
  USD: 1,
  QAR: 3.64,
  SAR: 3.75,
  AED: 3.6725,
  KWD: 0.307,
  BHD: 0.376,
  OMR: 0.385,
  EGP: 48.5,
  EUR: 0.92,
  GBP: 0.76,
};

const STORAGE_KEY = 'ticketaty_locale';
const RATES_KEY = 'ticketaty_rates';
const LocaleContext = createContext(null);

const guessLang = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const browserAr = (navigator.language || '').toLowerCase().startsWith('ar');
  const arabicZones = ['Asia/Qatar', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Bahrain', 'Asia/Muscat', 'Africa/Cairo'];
  if (browserAr || arabicZones.includes(tz)) return 'ar';
  return 'en';
};

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.lang !== 'ar' && parsed?.lang !== 'en') return null;
    if (!CURRENCIES.some((item) => item.code === parsed.currency)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const readRates = () => {
  try {
    const raw = localStorage.getItem(RATES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rates || Date.now() - parsed.at > 12 * 60 * 60 * 1000) return null;
    return parsed.rates;
  } catch {
    return null;
  }
};

const interpolate = (text, vars) => {
  if (!vars) return text;
  return Object.entries(vars).reduce(
    (out, [key, value]) => out.replaceAll(`{${key}}`, value ?? ''),
    text
  );
};

export const LocaleProvider = ({ children }) => {
  const stored = readStored();
  const [lang, setLangState] = useState(stored?.lang || guessLang());
  const [currency, setCurrencyState] = useState(stored?.currencyChosen ? stored.currency : 'QAR');
  const [langChosen, setLangChosen] = useState(!!stored?.manual || !!stored?.langChosen);
  const [currencyChosen, setCurrencyChosen] = useState(!!stored?.currencyChosen);
  const [rates, setRates] = useState(() => readRates() || FALLBACK_RATES);
  const hadStoredLang = useRef(!!stored?.lang).current;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ lang, currency, langChosen, currencyChosen }));
  }, [lang, currency, langChosen, currencyChosen]);

  useEffect(() => {
    document.documentElement.lang = lang === 'ar' ? 'ar' : 'en';
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    const loadRates = async () => {
      try {
        const response = await fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.min.json');
        const data = await response.json();
        if (!data?.usd || cancelled) return;
        const next = { USD: 1 };
        Object.entries(data.usd).forEach(([code, value]) => {
          next[code.toUpperCase()] = value;
        });
        setRates(next);
        localStorage.setItem(RATES_KEY, JSON.stringify({ at: Date.now(), rates: next }));
      } catch {
        /* fallback rates stay in place */
      }
    };
    loadRates();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (langChosen || hadStoredLang) return undefined;
    let cancelled = false;
    const detect = async () => {
      try {
        const response = await fetch('https://get.geojs.io/v1/ip/geo.json');
        const data = await response.json();
        if (cancelled || !data?.country) return;
        const country = String(data.country).toUpperCase();
        setLangState(ARAB_COUNTRIES.has(country) ? 'ar' : 'en');
      } catch {
        /* timezone guess remains */
      }
    };
    detect();
    return () => { cancelled = true; };
  }, [langChosen, hadStoredLang]);

  const setLang = (next) => {
    setLangChosen(true);
    setLangState(next === 'ar' ? 'ar' : 'en');
  };

  const setCurrency = (next) => {
    if (!CURRENCIES.some((item) => item.code === next)) return;
    setCurrencyChosen(true);
    setCurrencyState(next);
  };

  const value = useMemo(() => {
    const t = (key, vars) => interpolate(messages[lang]?.[key] ?? messages.en[key] ?? key, vars);

    const convert = (amount, fromCurrency) => {
      const number = Number(amount);
      if (!Number.isFinite(number)) return 0;
      const from = (fromCurrency || 'QAR').toUpperCase();
      const to = currency;
      if (from === to) return number;
      const fromRate = rates[from] || FALLBACK_RATES[from];
      const toRate = rates[to] || FALLBACK_RATES[to];
      if (!fromRate || !toRate) return number;
      return (number / fromRate) * toRate;
    };

    const money = (amount, fromCurrency = 'QAR') => {
      const from = (fromCurrency || 'QAR').toUpperCase();
      const value = convert(amount, from);
      const formatted = new Intl.NumberFormat('en', {
        style: 'currency',
        currency,
        currencyDisplay: 'code',
      }).format(value).replace(/[\u200e\u200f]/g, '');
      return from === currency ? formatted : `≈ ${formatted}`;
    };

    const formatWhen = (date, options) => {
      if (!date) return '';
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toLocaleDateString(lang === 'ar' ? 'ar-QA' : 'en-US', {
        ...options,
        numberingSystem: 'latn',
      });
    };

    return {
      lang,
      dir: lang === 'ar' ? 'rtl' : 'ltr',
      currency,
      setLang,
      setCurrency,
      t,
      money,
      convert,
      formatWhen,
    };
  }, [lang, currency, rates]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used within LocaleProvider');
  return context;
};
